import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Guest cart: product id + quantity, persisted to localStorage. No account,
 *  no server session — the cart travels to the guest checkout as ids +
 *  quantities and the server re-prices everything from the DB. */
export interface CartLine {
  productId: string;
  quantity: number;
}

interface CartState {
  installLocationId?: string;
  setInstallLocation: (id: string | undefined) => void;
  lines: CartLine[];
  add: (productId: string, qty?: number) => void;
  setQty: (productId: string, qty: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const MAX_QTY = 99;

export const useCart = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      setInstallLocation: (installLocationId) => set({ installLocationId }),
      add: (productId, qty = 1) =>
        set((s) => {
          const existing = s.lines.find((l) => l.productId === productId);
          if (existing) {
            return {
              lines: s.lines.map((l) =>
                l.productId === productId
                  ? { ...l, quantity: Math.min(MAX_QTY, l.quantity + qty) }
                  : l,
              ),
            };
          }
          return { lines: [...s.lines, { productId, quantity: Math.min(MAX_QTY, qty) }] };
        }),
      setQty: (productId, qty) =>
        set((s) => ({
          lines:
            qty <= 0
              ? s.lines.filter((l) => l.productId !== productId)
              : s.lines.map((l) =>
                  l.productId === productId ? { ...l, quantity: Math.min(MAX_QTY, qty) } : l,
                ),
        })),
      remove: (productId) => set((s) => ({ lines: s.lines.filter((l) => l.productId !== productId) })),
      clear: () => set({ lines: [], installLocationId: undefined }),
    }),
    { name: 'evi-shop-cart' },
  ),
);

/** Total quantity across all lines — the header badge count. */
export function cartCount(lines: CartLine[]): number {
  return lines.reduce((n, l) => n + l.quantity, 0);
}
